import type {
	IssueActivityType,
	IssueInterface,
	SortField,
} from '@supply-mover-2-0/shared';
import { LinkableEntityType } from '@supply-mover-2-0/shared';
import type { StandardListParams } from '../types/list';
import { ApiService } from './ApiService';

export interface IssueWithRelations extends IssueInterface {
	clients?: Array<{ id: number; name: string }>;
	vendors?: Array<{ id: number; name: string }>;
	merchandisers?: Array<{ id: number; name: string }>;
	/** PD-13705 — hydrated on GET /api/issues/:id when the issue was submitted via the e-commerce portal. */
	submitterPublicUser?: {
		id: number;
		firstName: string | null;
		lastName: string | null;
		email: string | null;
	} | null;
	// Decorated by crm-api issue.service (getIssueById + list transform)
	// from the joined assignee user row.
	assigneeUserName?: string | null;
}

export interface PaginatedIssueQueryResult {
	success: boolean;
	data?: Array<IssueWithRelations>;
	pagination?:
		| {
				page: number;
				limit: number;
				total: number;
				totalPages: number;
		  }
		| undefined;
	error?: string;
}

export interface IssueActivityItem {
	id: string;
	type: IssueActivityType;
	entityId?: number;
	entityType?: string;
	timestamp: string;
	primaryText: string;
	secondaryText?: string;
	description?: string;
	viewUrl?: string;
	canEdit?: boolean;
	metadata?: Record<string, unknown>;
	user?: {
		id: number;
		firstName: string;
		lastName: string;
	};
}

export interface LinkedEntityInfo {
	id: number;
	entityType: string;
	entityId: number;
	entityName?: string;
	entitySecondaryInfo?: string;
	createdDate: string;
}

export interface LinkResult {
	success: boolean;
	data?: LinkedEntityInfo;
	error?: string;
}

export interface PaginatedLinksResult {
	success: boolean;
	data?: Array<LinkedEntityInfo>;
	page?: number;
	limit?: number;
	total?: number;
	// Nested envelope for wrapQueryService consumers (the list footer
	// reads pagination.total); the flat fields above are kept for
	// existing readers.
	pagination?: {
		page: number;
		limit: number;
		total: number;
		totalPages: number;
	};
	error?: string;
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class IssueQueryService {
	/**
	 * URL path segment for each polymorphic parent type.
	 * Mirrors the `@Get('client/:clientId')` etc. routes on `IssueController`.
	 */
	private static readonly _LINKED_ENTITY_URL_SEGMENT: Partial<
		Record<LinkableEntityType, string>
	> = {
		[LinkableEntityType.CLIENT]: 'client',
		[LinkableEntityType.VENDOR]: 'vendor',
		[LinkableEntityType.SOURCING_REQUEST]: 'sourcing-request',
		[LinkableEntityType.VEHICLE]: 'vehicle',
		[LinkableEntityType.MERCHANDISER]: 'merchandiser',
	};

	/**
	 * Polymorphic fetch for issues linked to a parent entity (Client / Vendor /
	 * Sourcing Request). Each entity type has its own dedicated endpoint
	 * (`/issues/<entity-type>/:id`) backed by a single backend service method;
	 * the per-type delegators below preserve their original call signatures.
	 */
	public static async getIssuesByLinkedEntity(params: {
		entityType: LinkableEntityType;
		entityId: number;
		page?: number;
		limit?: number;
		search?: string;
		sortFields?: Array<SortField>;
		filters?: Record<string, unknown>;
	}): Promise<PaginatedIssueQueryResult> {
		const segment =
			IssueQueryService._LINKED_ENTITY_URL_SEGMENT[params.entityType];

		if (segment == null) {
			return {
				success: false,
				error: `getIssuesByLinkedEntity does not support entityType: ${params.entityType}`,
			};
		}

		const urlParams = new URLSearchParams();

		if (params.page != null) {
			urlParams.append('page', params.page.toString());
		}
		if (params.limit != null) {
			urlParams.append('limit', params.limit.toString());
		}
		if (params.search != null) {
			urlParams.append('search', params.search);
		}
		if (params.sortFields != null && params.sortFields.length > 0) {
			urlParams.append('sortFields', JSON.stringify(params.sortFields));
		}
		if (params.filters != null && Object.keys(params.filters).length > 0) {
			urlParams.append('filters', JSON.stringify(params.filters));
		}

		const queryString = urlParams.toString();
		const url = `/issues/${segment}/${params.entityId}${queryString.length > 0 ? `?${queryString}` : ''}`;

		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: Array<IssueWithRelations>;
				pagination?: {
					page: number;
					limit: number;
					total: number;
					totalPages: number;
				};
				error?: string;
			}>(url);

			if (response.success) {
				return {
					success: true,
					data: response.data ?? [],
					pagination: response.pagination,
				};
			}

			return {
				success: false,
				error: response.error ?? 'Failed to fetch issues',
			};
		} catch (error: unknown) {
			console.error(`Error fetching issues by ${params.entityType}:`, error);
			return {
				success: false,
				error: 'An unexpected error occurred while fetching issues',
			};
		}
	}

	public static async getIssuesByClientId(
		clientId: number,
		page?: number,
		limit?: number,
		search?: string,
		sortFields?: Array<SortField>,
		filters?: Record<string, unknown>,
	): Promise<PaginatedIssueQueryResult> {
		return IssueQueryService.getIssuesByLinkedEntity({
			entityType: LinkableEntityType.CLIENT,
			entityId: clientId,
			page,
			limit,
			search,
			sortFields,
			filters,
		});
	}

	public static async getIssuesByVendorId(
		vendorId: number,
		page?: number,
		limit?: number,
		search?: string,
		sortFields?: Array<SortField>,
		filters?: Record<string, unknown>,
	): Promise<PaginatedIssueQueryResult> {
		return IssueQueryService.getIssuesByLinkedEntity({
			entityType: LinkableEntityType.VENDOR,
			entityId: vendorId,
			page,
			limit,
			search,
			sortFields,
			filters,
		});
	}

	public static async getIssuesBySourcingRequestId(
		sourcingRequestId: number,
		page?: number,
		limit?: number,
		search?: string,
		sortFields?: Array<SortField>,
		filters?: Record<string, unknown>,
	): Promise<PaginatedIssueQueryResult> {
		return IssueQueryService.getIssuesByLinkedEntity({
			entityType: LinkableEntityType.SOURCING_REQUEST,
			entityId: sourcingRequestId,
			page,
			limit,
			search,
			sortFields,
			filters,
		});
	}

	public static async getIssuesByVehicleId(
		vehicleId: number,
		page?: number,
		limit?: number,
		search?: string,
		sortFields?: Array<SortField>,
		filters?: Record<string, unknown>,
	): Promise<PaginatedIssueQueryResult> {
		return IssueQueryService.getIssuesByLinkedEntity({
			entityType: LinkableEntityType.VEHICLE,
			entityId: vehicleId,
			page,
			limit,
			search,
			sortFields,
			filters,
		});
	}

	public static async getIssuesByMerchandiserId(
		merchandiserId: number,
		page?: number,
		limit?: number,
		search?: string,
		sortFields?: Array<SortField>,
		filters?: Record<string, unknown>,
	): Promise<PaginatedIssueQueryResult> {
		return IssueQueryService.getIssuesByLinkedEntity({
			entityType: LinkableEntityType.MERCHANDISER,
			entityId: merchandiserId,
			page,
			limit,
			search,
			sortFields,
			filters,
		});
	}

	public static async getIssues(
		params: StandardListParams = {},
	): Promise<PaginatedIssueQueryResult> {
		const urlParams = new URLSearchParams();

		if (params.page != null) {
			urlParams.append('page', params.page.toString());
		}

		if (params.pageSize != null) {
			urlParams.append('limit', params.pageSize.toString());
		}

		if (params.search != null) {
			urlParams.append('search', params.search);
		}

		if (params.sort != null && params.sort.length > 0) {
			urlParams.append('sortFields', JSON.stringify(params.sort));
		}

		if (
			params.filters != null &&
			Object.keys(params.filters as Record<string, unknown>).length > 0
		) {
			urlParams.append('filters', JSON.stringify(params.filters));
		}

		if (params.cacheKey != null) {
			urlParams.append('cacheKey', params.cacheKey);
		}

		if (params.cacheTTL != null) {
			urlParams.append('cacheTTL', params.cacheTTL.toString());
		}

		if (params.listId != null) {
			urlParams.append('listId', params.listId);
		}

		const queryString = urlParams.toString();
		const url = `/issues${queryString.length > 0 ? `?${queryString}` : ''}`;

		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: Array<IssueWithRelations>;
				pagination?: {
					page: number;
					limit: number;
					total: number;
					totalPages: number;
				};
				error?: string;
			}>(url);

			if (response.success) {
				return {
					success: true,
					data: response.data ?? [],
					pagination: response.pagination,
				};
			}

			return {
				success: false,
				error: response.error ?? 'Failed to fetch issues',
			};
		} catch (error: unknown) {
			console.error('Error fetching issues:', error);
			return {
				success: false,
				error: 'An unexpected error occurred while fetching issues',
			};
		}
	}

	public static async deleteIssue(
		issueId: number,
	): Promise<{ success: boolean; error: string | undefined }> {
		try {
			const response = await ApiService.apiDelete<{
				success: boolean;
				error?: string;
			}>(`/issues/${issueId}`);

			return {
				success: response.success,
				error: response.error,
			};
		} catch (error: unknown) {
			return {
				success: false,
				error:
					error instanceof Error ? error.message : 'Failed to delete issue',
			};
		}
	}

	public static async getIssue(
		issueId: number,
	): Promise<{ success: boolean; data?: IssueWithRelations; error?: string }> {
		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: IssueWithRelations;
				error?: string;
			}>(`/issues/${issueId}`);

			if (response.success) {
				return {
					success: true,
					data: response.data,
				};
			}

			return {
				success: false,
				error: response.error ?? 'Failed to fetch issue',
			};
		} catch (error: unknown) {
			console.error('Error fetching issue:', error);
			return {
				success: false,
				error: 'An unexpected error occurred while fetching issue',
			};
		}
	}

	public static async createIssueNote(
		issueId: number,
		content: string,
	): Promise<{ success: boolean; error?: string }> {
		try {
			const response = await ApiService.apiPost<{
				success: boolean;
				error?: string;
			}>(`/issues/${issueId}/note`, { note: content });

			return {
				success: response.success,
				error: response.error,
			};
		} catch (error: unknown) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Failed to create note',
			};
		}
	}

	// issueId is required so the URL carries the parent for server-side
	// ownership verification (see docs/claude/cross-parent-note-edit-fix.md).
	public static async updateIssueNote(
		issueId: number,
		noteId: number,
		content: string,
	): Promise<{ success: boolean; error?: string }> {
		try {
			const response = await ApiService.apiPut<{
				success: boolean;
				error?: string;
			}>(`/issues/${issueId}/notes/${noteId}`, { note: content });

			return {
				success: response.success,
				error: response.error,
			};
		} catch (error: unknown) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Failed to update note',
			};
		}
	}

	public static async deleteIssueNote(
		issueId: number,
		noteId: number,
	): Promise<{ success: boolean; error?: string }> {
		try {
			const response = await ApiService.apiDelete<{
				success: boolean;
				error?: string;
			}>(`/issues/${issueId}/notes/${noteId}`);

			return {
				success: response.success,
				error: response.error,
			};
		} catch (error: unknown) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Failed to delete note',
			};
		}
	}

	public static async getIssueClients(issueId: number): Promise<{
		success: boolean;
		data?: Array<{ id: number; name: string; email?: string; phone?: string }>;
		error?: string;
	}> {
		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: Array<{
					id: number;
					name: string;
					email?: string;
					phone?: string;
				}>;
				error?: string;
			}>(`/issues/${issueId}/clients`);

			if (response.success) {
				return {
					success: true,
					data: response.data ?? [],
				};
			}

			return {
				success: false,
				error: response.error ?? 'Failed to fetch issue clients',
			};
		} catch (error: unknown) {
			console.error('Error fetching issue clients:', error);
			return {
				success: false,
				error: 'An unexpected error occurred while fetching issue clients',
			};
		}
	}

	public static async getIssueVendors(issueId: number): Promise<{
		success: boolean;
		data?: Array<{ id: number; name: string; email?: string; phone?: string }>;
		error?: string;
	}> {
		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: Array<{
					id: number;
					name: string;
					email?: string;
					phone?: string;
				}>;
				error?: string;
			}>(`/issues/${issueId}/vendors`);

			if (response.success) {
				return {
					success: true,
					data: response.data ?? [],
				};
			}

			return {
				success: false,
				error: response.error ?? 'Failed to fetch issue vendors',
			};
		} catch (error: unknown) {
			console.error('Error fetching issue vendors:', error);
			return {
				success: false,
				error: 'An unexpected error occurred while fetching issue vendors',
			};
		}
	}

	public static async createIssue(
		issue: {
			name: string;
			description?: string | null;
			status?: string;
			type?: string | null;
			assigneeUserId?: number | null;
		},
		clientIds?: Array<number>,
		vendorIds?: Array<number>,
	): Promise<{ success: boolean; data?: IssueWithRelations; error?: string }> {
		try {
			const response = await ApiService.apiPost<{
				success: boolean;
				data?: IssueWithRelations;
				error?: string;
			}>('/issues', { issue, clientIds, vendorIds });

			if (response.success) {
				return {
					success: true,
					data: response.data,
				};
			}

			return {
				success: false,
				error: response.error ?? 'Failed to create issue',
			};
		} catch (error: unknown) {
			return {
				success: false,
				error:
					error instanceof Error ? error.message : 'Failed to create issue',
			};
		}
	}

	public static async updateIssue(
		issueId: number,
		issue: {
			name: string;
			description?: string | null;
			status?: string;
			type?: string | null;
			priority?: string;
			assigneeUserId?: number | null;
		},
	): Promise<{ success: boolean; data?: IssueWithRelations; error?: string }> {
		try {
			const response = await ApiService.apiPut<{
				success: boolean;
				data?: IssueWithRelations;
				error?: string;
			}>(`/issues/${issueId}`, { issue });

			if (response.success) {
				return {
					success: true,
					data: response.data,
				};
			}

			return {
				success: false,
				error: response.error ?? 'Failed to update issue',
			};
		} catch (error: unknown) {
			return {
				success: false,
				error:
					error instanceof Error ? error.message : 'Failed to update issue',
			};
		}
	}

	public static async getIssueWatchers(issueId: number): Promise<{
		success: boolean;
		data?: Array<{ id: number; firstName: string; lastName: string }>;
		error?: string;
	}> {
		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: Array<{ id: number; firstName: string; lastName: string }>;
				error?: string;
			}>(`/issues/${issueId}/watchers`);

			if (response.success) {
				return {
					success: true,
					data: response.data ?? [],
				};
			}

			return {
				success: false,
				error: response.error ?? 'Failed to fetch watchers',
			};
		} catch (error: unknown) {
			return {
				success: false,
				error:
					error instanceof Error ? error.message : 'Failed to fetch watchers',
			};
		}
	}

	public static async addIssueWatcher(
		issueId: number,
		userId: number,
	): Promise<{ success: boolean; error?: string }> {
		try {
			const response = await ApiService.apiPost<{
				success: boolean;
				error?: string;
			}>(`/issues/${issueId}/watcher/${userId}`, {});

			return {
				success: response.success,
				error: response.error,
			};
		} catch (error: unknown) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Failed to add watcher',
			};
		}
	}

	public static async removeIssueWatcher(
		issueId: number,
		userId: number,
	): Promise<{ success: boolean; error?: string }> {
		try {
			const response = await ApiService.apiDelete<{
				success: boolean;
				error?: string;
			}>(`/issues/${issueId}/watcher/${userId}`);

			return {
				success: response.success,
				error: response.error,
			};
		} catch (error: unknown) {
			return {
				success: false,
				error:
					error instanceof Error ? error.message : 'Failed to remove watcher',
			};
		}
	}

	public static async getIssueLinks(
		issueId: number,
		entityType?: string,
	): Promise<{
		success: boolean;
		data?: Array<LinkedEntityInfo>;
		error?: string;
	}> {
		const params = new URLSearchParams();

		if (entityType != null) {
			params.append('entityType', entityType);
		}

		const queryString = params.toString();
		const url = `/issues/${issueId}/links${queryString.length > 0 ? `?${queryString}` : ''}`;

		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: Array<LinkedEntityInfo>;
				error?: string;
			}>(url);

			if (response.success) {
				return {
					success: true,
					data: response.data ?? [],
				};
			}

			return {
				success: false,
				error: response.error ?? 'Failed to fetch links',
			};
		} catch (error: unknown) {
			console.error('Error fetching issue links:', error);

			return {
				success: false,
				error: 'An unexpected error occurred while fetching links',
			};
		}
	}

	public static async linkEntityToIssue(
		issueId: number,
		entityType: string,
		entityId: number,
		entityName?: string,
	): Promise<LinkResult> {
		try {
			const response = await ApiService.apiPost<LinkResult>(
				`/issues/${issueId}/link`,
				{ entityType, entityId, entityName },
			);

			if (response.success) {
				return {
					success: true,
					data: response.data,
				};
			}

			return {
				success: false,
				error: response.error ?? 'This record is already linked to this issue',
			};
		} catch (error: unknown) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Failed to create link',
			};
		}
	}

	public static async unlinkEntityFromIssue(
		issueId: number,
		entityType: string,
		entityId: number,
		entityName?: string,
	): Promise<{ success: boolean; error?: string }> {
		try {
			const url =
				entityName != null
					? `/issues/${issueId}/link/${entityType}/${entityId}?entityName=${encodeURIComponent(entityName)}`
					: `/issues/${issueId}/link/${entityType}/${entityId}`;

			const response = await ApiService.apiDelete<{
				success: boolean;
				error?: string;
			}>(url);

			return {
				success: response.success,
				error: response.error,
			};
		} catch (error: unknown) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Failed to remove link',
			};
		}
	}

	public static async getIssueLinksEnriched(
		issueId: number,
		params: StandardListParams = {},
	): Promise<PaginatedLinksResult> {
		const urlParams = new URLSearchParams();

		if (params.page != null) {
			urlParams.append('page', params.page.toString());
		}

		if (params.pageSize != null) {
			urlParams.append('limit', params.pageSize.toString());
		}

		if (params.search != null) {
			urlParams.append('search', params.search);
		}

		const filters = params.filters as Record<string, unknown> | undefined;

		if (filters?.entityType != null) {
			urlParams.append('entityType', filters.entityType as string);
		}

		const queryString = urlParams.toString();
		const url = `/issues/${issueId}/links-list${queryString.length > 0 ? `?${queryString}` : ''}`;

		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: Array<LinkedEntityInfo>;
				pagination?: {
					page: number;
					limit: number;
					total: number;
					totalPages: number;
				};
				error?: string;
			}>(url);

			if (response.success) {
				const rows = response.data ?? [];
				// The wire sends total as a string; coerce so footers render
				// real numbers. Fall back to the row count when the envelope
				// is absent (endpoint returns all rows in that case).
				const pagination =
					response.pagination != null
						? {
								page: Number(response.pagination.page),
								limit: Number(response.pagination.limit),
								total: Number(response.pagination.total),
								totalPages: Number(response.pagination.totalPages),
							}
						: {
								page: 1,
								limit: rows.length,
								total: rows.length,
								totalPages: 1,
							};

				return {
					success: true,
					data: rows,
					page: pagination.page,
					limit: pagination.limit,
					total: pagination.total,
					pagination,
				};
			}

			return {
				success: false,
				error: response.error ?? 'Failed to fetch linked records',
			};
		} catch (error: unknown) {
			console.error('Error fetching enriched links:', error);

			return {
				success: false,
				error: 'An unexpected error occurred while fetching linked records',
			};
		}
	}

	public static async getIssueActivities(
		issueId: number,
		limit: number = 50,
		offset: number = 0,
		types?: Array<string>,
	): Promise<{
		success: boolean;
		data?: {
			activities: Array<{
				id: number;
				activityType: string;
				entityType: string | null;
				entityId: number | null;
				description: string | null;
				metadata: Record<string, unknown> | null;
				createdDate: string;
				fkUser?: {
					id: number;
					firstName: string;
					lastName: string;
				};
			}>;
			total: number;
		};
		error?: string;
	}> {
		const params = new URLSearchParams();
		params.append('limit', String(limit));
		params.append('offset', String(offset));

		if (types != null && types.length > 0) {
			params.append('types', types.join(','));
		}

		const url = `/issues/${issueId}/activities?${params.toString()}`;

		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: {
					activities: Array<{
						id: number;
						activityType: string;
						entityType: string | null;
						entityId: number | null;
						description: string | null;
						metadata: Record<string, unknown> | null;
						createdDate: string;
						fkUser?: {
							id: number;
							firstName: string;
							lastName: string;
						};
					}>;
					total: number;
				};
				error?: string;
			}>(url);

			if (response.success) {
				return {
					success: true,
					data: response.data,
				};
			}

			return {
				success: false,
				error: response.error ?? 'Failed to fetch activities',
			};
		} catch (error: unknown) {
			return {
				success: false,
				error:
					error instanceof Error ? error.message : 'Failed to fetch activities',
			};
		}
	}
}
